import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { Button, Input } from "@quietbuildlab/ui";
import { signIn, signUp } from "../lib/auth-client";
import { apiGet } from "../lib/api";

const LoginSearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: LoginSearchSchema,
  component: LoginPage,
});

function safeNext(next: string | undefined): string {
  if (!next) return "/";
  if (next.startsWith("/") && !next.startsWith("//")) return next;
  if (typeof window === "undefined") return "/";
  try {
    const url = new URL(next);
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}`;
  } catch {
    /* ignore */
  }
  return "/";
}

function LoginPage() {
  const router = useRouter();
  const search = Route.useSearch();
  const next = safeNext(search.next);
  const [mode, setMode] = useState<"in" | "up">("in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    apiGet<{ googleEnabled?: boolean }>("/api/config")
      .then((cfg) => setGoogleEnabled(Boolean(cfg.googleEnabled)))
      .catch(() => setGoogleEnabled(false));
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const result =
      mode === "up"
        ? await signUp.email({ name, email, password, callbackURL: next })
        : await signIn.email({ email, password, callbackURL: next });
    setBusy(false);
    if (result.error) {
      setError(result.error.message ?? "Could not sign in");
      return;
    }
    await router.invalidate();
    window.location.assign(next);
  }

  return (
    <main className="mx-auto w-full max-w-md px-5 py-16">
      <h1 className="text-2xl font-semibold tracking-tight mb-2">
        {mode === "up" ? "Create account" : "Sign in"}
      </h1>
      <p className="text-sm text-[color:var(--color-fg-muted)] mb-8">
        Use an email and password. Passwords are stored as a hash.
      </p>

      <form className="space-y-3" onSubmit={onSubmit}>
        {mode === "up" && (
          <Input
            required
            autoComplete="name"
            placeholder="Name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        )}
        <Input
          required
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          required
          type="password"
          autoComplete={mode === "up" ? "new-password" : "current-password"}
          placeholder="Password"
          minLength={8}
          maxLength={128}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && <p className="text-sm text-[color:var(--color-red)]">{error}</p>}
        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? "Please wait…" : mode === "up" ? "Create account" : "Sign in"}
        </Button>
      </form>

      <button
        type="button"
        className="mt-4 text-sm text-[color:var(--color-fg-muted)] hover:underline"
        onClick={() => {
          setMode(mode === "up" ? "in" : "up");
          setError(null);
        }}
      >
        {mode === "up" ? "Already have an account? Sign in" : "New here? Create an account"}
      </button>

      {googleEnabled && (
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full mt-8 bg-[color:var(--color-panel)] hover:bg-[color:var(--color-panel-2)]"
          onClick={async () => {
            await signIn.social({ provider: "google", callbackURL: next });
            router.invalidate();
          }}
        >
          <span>Continue with Google</span>
        </Button>
      )}
    </main>
  );
}
